import React, { useState } from 'react';

import moment from 'moment-timezone';

import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Grid2 as Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { Loading } from '../../common/components/Loading';
import { Tag } from '../../common/components/Tag';
import { Resource } from '../../common/constants/resource';
import { factsExist } from '../detail/utilities/taskDetailUtilities';
import { CriminalRecordsFacts } from '../tasks/types/criminalRecords';
import { Evidence } from '../tasks/types/evidence';
import { TaskTypes } from '../tasks/types/taskTypes';
import { fetchEvidenceData } from './helpers';

export const SherlockDetail = ({
  product,
  carrier,
  policyId,
  evidenceId,
}: {
  product: string;
  carrier: string;
  policyId: string;
  evidenceId: number;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [evidence, setEvidence] = useState<Evidence | null>(null);
  const [evidenceInprogress, setEvidenceInprogress] = useState(false);
  const [evidenceReady, setEvidenceReady] = useState(false);
  const doExpand = async () => {
    if (!expanded) {
      if (!evidenceReady && !evidenceInprogress) {
        // fetch ruleResultData
        setEvidenceInprogress(true);
        const result = await fetchEvidenceData(
          product,
          carrier,
          policyId,
          evidenceId.toString(),
          TaskTypes.CRIMINAL_RECORDS,
        );
        setEvidence(result);
        setEvidenceReady(true);
        setEvidenceInprogress(false);
      }
    }
    setExpanded(!expanded);
  };

  function renderTaskDetails() {
    const facts: CriminalRecordsFacts = evidence?.facts as CriminalRecordsFacts;
    const errorMessage = evidence?.errorMessage;

    if (errorMessage) {
      return (
        <Box mt={2}>
          <Typography variant="body2">{errorMessage}</Typography>
        </Box>
      );
    }
    if (facts) {
      return (
        <>
          <Box mt={2}>
            <Grid container spacing={1}>
              <>
                <Grid spacing={{ xs: 3 }}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {Resource.CRIMINAL_RECORDS_RESULT}
                  </Typography>
                </Grid>
                <Grid spacing={{ xs: 9 }}>
                  <Box ml={1.5}>
                    {facts.recordExists ? (
                      <Tag color="LighterBlue">HIT</Tag>
                    ) : (
                      <Tag color="LightOrange">NO HIT</Tag>
                    )}
                  </Box>
                </Grid>
              </>
            </Grid>
          </Box>
          {facts.recordExists && (
            <Box mt={4}>
              <TableContainer component={Paper} variant="outlined" square>
                <Table aria-label="Decisions table">
                  <TableHead>
                    <TableRow>
                      <TableCell width={120}>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_CATEGORY}
                        </Typography>
                      </TableCell>
                      <TableCell width={130}>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_SUBCATEGORY}
                        </Typography>
                      </TableCell>
                      <TableCell width={160}>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_DESCRIPTION}
                        </Typography>
                      </TableCell>
                      <TableCell width={100}>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_SEVERITY}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_DISPOSITION}
                        </Typography>
                      </TableCell>
                      <TableCell width={120}>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_DATE}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_SOURCE}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {facts.offenses.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Typography variant="body1">
                            {row.categoryName ?? Resource.NORA_M_DASH}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {(row.offenseSubcategoryName ||
                              row.offenseSubcategoryCode) ??
                              Resource.NORA_M_DASH}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.description ?? Resource.NORA_M_DASH}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {(row.severityDescription || row.severityType) ??
                              Resource.NORA_M_DASH}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {(row.dispositionDescription ||
                              row.dispositionType) ??
                              Resource.NORA_M_DASH}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="body2">
                              {row.recordDateType}
                            </Typography>
                            <Typography variant="body2">
                              {row.recordDate
                                ? moment(row.recordDate, 'YYYY-MM-DD').format(
                                    'YYYY/MM/DD',
                                  )
                                : Resource.NORA_M_DASH}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.source ?? Resource.NORA_M_DASH}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      );
    } else {
      return <></>;
    }
  }

  return (
    <Box pt={8}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        ml={-1}
      >
        <Box display="flex" flexDirection="row">
          <Box mt={-0.15} ml={-3.5} position="absolute">
            <IconButton
              style={{ width: '24px' }}
              size="small"
              onClick={doExpand}
            >
              <FontAwesomeIcon
                icon={faAngleRight}
                rotation={expanded ? 90 : undefined}
              />
            </IconButton>
          </Box>
          <Typography variant="h2">CRIMINAL RECORDS</Typography>
        </Box>
      </Box>

      {expanded &&
        (evidenceInprogress ? (
          <Loading fullscreen={false} />
        ) : (
          evidenceReady &&
          (evidence ? (
            factsExist(evidence) ? (
              renderTaskDetails()
            ) : (
              <></>
            )
          ) : (
            <Box>
              <Typography variant="body1">
                failed to load criminal records data
              </Typography>
            </Box>
          ))
        ))}
    </Box>
  );
};
